/**
 * Getting a photo of the label.
 *
 * Camera where it exists, file picker everywhere else — and the file picker is
 * not a lesser fallback: on a desktop it is the only route, and on a phone it
 * lets someone use a photo they already took in the shop.
 *
 * The live preview does a cheap quality check a few times a second so the
 * guidance arrives BEFORE the shutter rather than after a failed scan. That is
 * the difference between "move closer" and "we couldn't read that, try again".
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { assessQuality } from '@/lib/scanner/image-quality';
import type { QualityVerdict } from '@/lib/scanner/types';

/** How often the live frame is graded. Cheap, but not free. */
const GUIDANCE_INTERVAL_MS = 700;
/** The live check runs on a small copy; full resolution is only for capture. */
const GUIDANCE_WIDTH = 320;
/** Cap on the captured image, so a 108-megapixel phone does not stall the worker. */
const CAPTURE_MAX_WIDTH = 1800;

export function CameraCapture({
  onCapture,
  onCancel,
}: {
  onCapture: (image: ImageData) => void;
  onCancel: () => void;
}): React.JSX.Element {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const [live, setLive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [guidance, setGuidance] = useState<QualityVerdict | null>(null);

  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setLive(false);
  }, []);

  const start = useCallback(async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          // The rear camera, and a resolution high enough for small print.
          facingMode: { ideal: 'environment' },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setLive(true);
    } catch (err) {
      setError(
        err instanceof DOMException && err.name === 'NotAllowedError'
          ? 'Camera access was declined. You can still choose a photo instead.'
          : 'No camera is available here. Choose a photo instead.',
      );
    }
  }, []);

  // Release the camera on unmount. Leaving it running keeps the indicator
  // light on, which reasonably alarms people.
  useEffect(() => stop, [stop]);

  // Live guidance while the preview is running.
  useEffect(() => {
    if (!live) return;
    const timer = window.setInterval(() => {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (!video || !canvas || video.videoWidth === 0) return;

      const scale = GUIDANCE_WIDTH / video.videoWidth;
      canvas.width = GUIDANCE_WIDTH;
      canvas.height = Math.round(video.videoHeight * scale);
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) return;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      setGuidance(assessQuality(ctx.getImageData(0, 0, canvas.width, canvas.height)));
    }, GUIDANCE_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [live]);

  const capture = (): void => {
    const video = videoRef.current;
    if (!video || video.videoWidth === 0) return;
    const scale = Math.min(1, CAPTURE_MAX_WIDTH / video.videoWidth);
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const image = ctx.getImageData(0, 0, canvas.width, canvas.height);
    stop();
    onCapture(image);
  };

  const pickFile = async (file: File): Promise<void> => {
    setError(null);
    try {
      const bitmap = await createImageBitmap(file);
      const scale = Math.min(1, CAPTURE_MAX_WIDTH / bitmap.width);
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(bitmap.width * scale);
      canvas.height = Math.round(bitmap.height * scale);
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) return;
      ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      bitmap.close();
      stop();
      onCapture(ctx.getImageData(0, 0, canvas.width, canvas.height));
    } catch {
      setError('That file could not be read as an image.');
    }
  };

  const hint = guidance?.advice ?? (guidance ? 'Looks good — take the photo.' : null);
  const ready = guidance !== null && guidance.advice === null;

  return (
    <div className="scan-camera">
      <div className="scan-stage">
        <video
          ref={videoRef}
          className="scan-video"
          playsInline
          muted
          // Not mirrored: this is the rear camera reading printed text.
          aria-label="Camera preview"
        />
        {/* The frame is guidance, not a crop — the panel is found automatically. */}
        <div className={`scan-frame${ready ? ' is-ready' : ''}`} aria-hidden="true">
          <span className="scan-frame-label">Nutrition label</span>
        </div>
        {!live && (
          <div className="scan-stage-empty">
            <p>{error ?? 'Point the camera at the nutrition label.'}</p>
          </div>
        )}
      </div>

      {hint !== null && live && (
        <p className={`scan-hint${ready ? ' is-ready' : ''}`} aria-live="polite">
          {hint}
        </p>
      )}

      <canvas ref={canvasRef} className="visually-hidden" aria-hidden="true" />

      <div className="scan-actions">
        {!live ? (
          <button type="button" className="btn btn-primary" onClick={() => void start()}>
            Open camera
          </button>
        ) : (
          <button type="button" className="btn btn-primary" onClick={capture}>
            Take photo
          </button>
        )}

        <button type="button" className="btn" onClick={() => fileRef.current?.click()}>
          Choose a photo
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="visually-hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void pickFile(file);
            e.target.value = '';
          }}
        />

        <button type="button" className="btn" onClick={() => { stop(); onCancel(); }}>
          Cancel
        </button>
      </div>

    </div>
  );
}
